import { useQuery } from "@tanstack/react-query"
import { App, Button, Checkbox, Col, Flex, GetProp, Input, Radio, Row, Skeleton, Space, Tooltip, Upload, UploadProps } from "antd"
import TextArea from "antd/es/input/TextArea"
import { useCallback, useEffect, useMemo, useState } from "react"
import styled, { useTheme } from "styled-components"
import * as XLSX from 'xlsx'
import { ShowMessagePage } from "../../config"
import { getProducts } from "../../endpoints"
import { OrderInfoInterface, SampleInterface } from "../../types"
import { ContainerFull, GrayLabel, LabeledBox } from "../GeneralComponents"

interface EnterSampleInformationProps {
    orderInfo: OrderInfoInterface
    setOrderInfo: React.Dispatch<React.SetStateAction<OrderInfoInterface>>
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>
    enterSampleNames?: boolean
}
type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0]

const validateSampleNames = (namesString: string): string => {
    const names = namesString.trim().replace(/\n+$/, "").trim() //remove all trailing \n in the input

    const illegalCharacters = names.match(/[^a-zA-Z0-9\-_\n.]/g)
    const sizes = names
        .split("\n")
        .map((x) => x.trim())
        .map((x) => x.length)
    const maxl = Math.max(...sizes)
    // console.log(names)

    if (illegalCharacters)
        return "sorry, " + illegalCharacters + (illegalCharacters.length > 1 ? " are" : " is") + " not allowed in sample names"
    else if (maxl > 72) {
        return "sorry, names cannot exceed 72 characters"
    } else {
        return ""
    }
}


const validateSampleSequences = (sequenceString: string): string => {
    const sequences = sequenceString.trim().replace(/\n+$/, "").trim() //remove all trailing \n in the input

    const illegalCharacters = sequences.match(/[^agctAGCT]/g)
   
    // console.log(names)

    if (illegalCharacters)
        return "sorry, " + illegalCharacters + (illegalCharacters.length > 1 ? " are" : " is") + " not allowed in DNA sequences"
     else {
        return ""
    }
}

const BigColoredCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 2.5px solid #388087 !important;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #e6f7ff !important;
    border-color: #388087 !important;
  }
  .ant-checkbox-inner::after {
    border-color: #388087 !important; /* dark red */
    left: 50%;
    top: 50%;
    width: 8px;
    height: 16px;
    border-width: 0 3px 3px 0;
    transform: translate(-50%, -50%) rotate(45deg) scale(1.1);
  }
  
`

const parseFasta = (fastaContent: string): SampleInterface[] => {
    const lines = fastaContent.split(/\r?\n/) // Split into lines, handling both \n and \r\n
    const records: SampleInterface[] = []
    let currentHeader: string | null = null
    let currentSequence: string[] = []

    for (const line of lines) {
        if (line.startsWith(">")) {
            // Save the previous record if it exists
            if (currentHeader) {
                const fields = currentHeader.split("|").map((x) => x.trim())
                const sampleName = fields[0]
                let fullLength = true
                if (fields.length > 1 && fields[1].toLocaleLowerCase().indexOf("partial") > -1) {
                    fullLength = false
                }
                records.push({
                    sampleName: sampleName,
                    sequence: currentSequence.join(""),
                    fullLength,
                })
            }
            // Start a new record
            currentHeader = line.slice(1).trim() // Remove the leading `>` and trim whitespace
            currentSequence = []
        } else if (line.trim()) {
            // Add sequence lines (non-empty)
            currentSequence.push(line.trim())
        }
    }

    // Push the last record if it exists
    if (currentHeader) {
        const fields = currentHeader.split("|").map((x) => x.trim())
        const sampleName = fields[0]
        let fullLength = true
        if (fields.length > 1 && fields[1].toLocaleLowerCase().indexOf("partial") > -1) {
            fullLength = false
        }
        records.push({
            sampleName: sampleName,
            sequence: currentSequence.join(""),
            fullLength,
        })
    }
    return records
}
const exampleInput2 = `>S-1
GAGATACCTACAGCGTGAGCTATGAGAAAGCGCCACGCTTCCCGAAGGGAGAAAGGCGGACAGGTATCCGGTAAGCGGC
AGGGTCGGAACAGGAGAGCGCACGAGGGAGCTTCCAGGGGGAAACGCCTGGTATCTTTATAGTCCTGTCGGGTTTCGCC
ACCTCTGACTTGAGCGTCGATTTTTGTGATGCTCGTCAGGGGGGCGGAGCCTATGGAAAAACGCCA
>S-2
GCACTTTTAAAGTTCTGCTATGTGGCGCGGTATTATCCCGTATTGACGCCGGGCAAGAGCAACTCGGTCGCCGCATACA
CTATTCTCAGAATGACTTGGTTGAGTACTCACCAGTCACAGAAAAGCATCTTACGGATGGCATGACAGTAAGAGAATTA
TGCAGTGCTGCCATAACCATGAGTGATAACACTGCGGCCAACTTACTTCTGACAACGATCGGAGGA
>S-3
TTTGATCTGGGTAAAAATAAGAGGGTCACGGTAAGACAATTTAGAAACATAAATTTAATTGATATCCGTGAGTACTATC
TGGACTCATCTACAGGCGAAATGAAACCTGGGAAGAAAGGTATTTCGTTGACAGAAGATCTATACGA    
`

export const EnterSampleInformationAmpliconSeq = (props: EnterSampleInformationProps): JSX.Element => {
    const { modal } = App.useApp()
    const theme = useTheme()
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [showSampleNameError, setShowSampleNameError] = useState<boolean>(false)
    
    // Destructure props to avoid dependency issues in useEffect
    const { orderInfo, setOrderInfo } = props
    
    // Create a stable reference to the setOrderInfo function to avoid issues with samples comparison
    const updateSamples = useCallback((newSamples: SampleInterface[]) => {
        setOrderInfo(prev => ({
            ...prev,
            samples: newSamples,
        }))
    }, [setOrderInfo])
    useEffect(() => {
        const names = orderInfo.samples?.map((x) => x.sampleName) ?? []
        const illegalCharacters = names.join("").match(/[^a-zA-Z0-9\-_\n.]/g)
        if (illegalCharacters) {
            setErrorMessage(
                "sorry, " +
                    illegalCharacters +
                    (illegalCharacters.length > 1 ? " are" : " is") +
                    " not allowed in sample names"
            )
        } else if (new Set(names).size !== names.length) {
            setErrorMessage("Repeated sample names detected")
        } else {
            const sequences = orderInfo.samples?.map((x) => x.sequence) ?? []
            const illegalBases = sequences.join("").match(/[^agctAGCT]/g)
            if (illegalBases) {
                setErrorMessage(
                    illegalBases + (illegalBases.length > 1 ? " are" : " is") + " not allowed in DNA sequences"
                )
            } else {
                setErrorMessage("")
            }
        }
    }, [orderInfo.samples])
    
    // Use useMemo to compute merged samples without causing infinite loops
    const mergedSamples = useMemo(() => {
        let samples1: SampleInterface[] = []
        if (orderInfo.sample_names && orderInfo.sample_names.length > 0) {
            const names = orderInfo.sample_names
                .trim()
                .replace(/\n+$/, "")
                .split("\n")
                .map((x) => x.trim())
                .filter((x) => x.length > 0)
            samples1 = names.map((name) => {
                return {
                    sampleName: name,
                    sequence: "",
                    fullLength: true,
                }
            })
        }
        
        let samples2: SampleInterface[] = []
        if (orderInfo.sample_sequences) {
            samples2 = parseFasta(orderInfo.sample_sequences)
        } 
        
        const merged = [...samples2]
        for (const sample of samples1) {
            if (!samples2.find((s) => s.sampleName.toLowerCase() === sample.sampleName.toLowerCase())) {
                merged.push(sample)
            }
        }
        
        return merged
    }, [orderInfo.sample_names, orderInfo.sample_sequences])

    // Update orderInfo when mergedSamples changes
    useEffect(() => {
        const currentSamples = orderInfo.samples || []
        const samplesChanged = JSON.stringify(currentSamples) !== JSON.stringify(mergedSamples)
        
        if (samplesChanged) {
            updateSamples(mergedSamples)
        }
    }, [mergedSamples, orderInfo.samples, updateSamples])

    //   console.log('orderInfo:',props.orderInfo)
    const { isPending, isError, error, isSuccess } = useQuery({
        queryKey: ["getproducts"],
        queryFn: getProducts,
    })
    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error.message)
    }
    if (isSuccess) {
        // console.log(props.orderInfo)
    }

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            {orderInfo.productid != null && (
                <div style={{ borderStyle: " none" }}>
                    {/* <div style={{ 
                        color: "#ac6206ff", 
                        fontWeight: "bold", 
                        marginBottom: "1em",
                        padding: "12px",
                        backgroundColor: "#fff1f0",
                        border: "1px solid #ffccc7",
                        borderRadius: "4px"
                    }}>
                        Please note at this time we require template sequence for amplicon sequecing
                    </div> */}
                    <LabeledBox title="Instructions for sample preparation">
                        <ul
                            style={{
                                textAlign: "left",
                                listStylePosition: "inside",
                                listStyleType: "disc",
                            }}
                        >
                            <li style={{ lineHeight: "2rem" }}>
                                Size &lt; 30kb
                            </li>
                            <li style={{ lineHeight: "2rem" }}>
                                Concentration between 50-100ng/μl
                            </li>
                            <li style={{ lineHeight: "2rem" }}>
                                10μl each sample
                            </li>
                            <li style={{ lineHeight: "2rem" }}>
                                Submit in strip tubes or 96-well plates, label the tubes or plates
                            </li>
                        </ul>
                    </LabeledBox>
                    <LabeledBox title="Plate Submission (Optional)">
                        <Flex vertical gap={16} style={{ marginTop: "1em" }}>
                            <BigColoredCheckbox
                                checked={orderInfo.submittingPlate || false}
                                onChange={(e) => {
                                    setOrderInfo({
                                        ...orderInfo,
                                        submittingPlate: e.target.checked,
                                    })
                                }}
                            >
                                <span style={{ fontSize: "16px", fontWeight: 500 }}>
                                    I am submitting samples in a 96-well plate
                                </span>
                            </BigColoredCheckbox>

                            {orderInfo.submittingPlate && (
                                <>
                                    <div style={{ marginLeft: "36px" }}>
                                        <div style={{ marginBottom: "12px", fontWeight: 500 }}>Plate filling order:</div>
                                        <Radio.Group
                                            value={orderInfo.plateOrder || 'column'}
                                            onChange={(e) => {
                                                setOrderInfo({
                                                    ...orderInfo,
                                                    plateOrder: e.target.value
                                                })
                                            }}
                                        >
                                            <Space direction="vertical">
                                                <Radio value="column">Column order (A1, B1, C1, ... H1, A2, B2, ...)</Radio>
                                                <Radio value="row">Row order (A1, A2, A3, ... A12, B1, B2, ...)</Radio>
                                            </Space>
                                        </Radio.Group>
                                        <div style={{ marginTop: "16px", color: "#2a6a73", fontSize: "16px", lineHeight: "1.6" }}>
                                            Samples should be arranged continuously well-by-well. <br/>
                                            For non-continuous arrangements, please email us a plate map (Excel table with columns "sample name" and "well position", e.g., A1, B3, etc.). <br/>
                                            For orders across multiple plates, first plates should be full plates, only the last plate can be partial. <br/>
                                            Note: custom plate maps may delay order processing.
                                        
                                        
                                        </div>
                                    </div>

                                    {orderInfo.samples && orderInfo.samples.length > 0 && (() => {
                                        const totalSamples = orderInfo.samples.length
                                        const numPlates = Math.ceil(totalSamples / 96)
                                        
                                        return (
                                            <div style={{ marginTop: "20px", maxWidth: "800px" }}>
                                                <div style={{ fontWeight: "500", marginBottom: "8px", color: "#333", fontSize: "14px" }}>
                                                    Plate Layout Preview ({totalSamples} samples, {numPlates} plate{numPlates > 1 ? 's' : ''}):
                                                </div>
                                                
                                                {Array.from({ length: numPlates }).map((_, plateIndex) => {
                                                    const startSampleIndex = plateIndex * 96
                                                    const endSampleIndex = Math.min(startSampleIndex + 96, totalSamples)
                                                    const samplesInPlate = endSampleIndex - startSampleIndex
                                                    
                                                    return (
                                                        <div key={`plate-${plateIndex}`} style={{ marginBottom: numPlates > 1 ? "24px" : "0" }}>
                                                            {numPlates > 1 && (
                                                                <div style={{ 
                                                                    fontWeight: "600", 
                                                                    marginBottom: "8px", 
                                                                    color: "#388087", 
                                                                    fontSize: "13px" 
                                                                }}>
                                                                    Plate {plateIndex + 1} ({samplesInPlate} samples)
                                                                </div>
                                                            )}
                                                            <div style={{ 
                                                                display: "grid", 
                                                                gridTemplateColumns: "24px repeat(12, minmax(45px, 1fr))", 
                                                                gap: "2px",
                                                                fontSize: "9px",
                                                                backgroundColor: "#f5f5f5",
                                                                padding: "6px",
                                                                borderRadius: "4px",
                                                                border: "1px solid #d9d9d9"
                                                            }}>
                                                                {/* Column headers */}
                                                                <div style={{ fontWeight: "bold", textAlign: "center" }}></div>
                                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(col => (
                                                                    <div key={`col-${col}`} style={{ fontWeight: "bold", textAlign: "center", padding: "2px", fontSize: "9px" }}>
                                                                        {col}
                                                                    </div>
                                                                ))}
                                                                
                                                                {/* Plate wells */}
                                                                {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((row, rowIndex) => (
                                                                    <>
                                                                        {/* Row label */}
                                                                        <div key={`row-${row}`} style={{ fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                            {row}
                                                                        </div>
                                                                        
                                                                        {/* Wells in this row */}
                                                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((col, colIndex) => {
                                                                            const wellPosition = `${row}${col}`
                                                                            let sampleIndexInPlate = -1
                                                                            
                                                                            // Calculate sample index based on order type
                                                                            if (orderInfo.plateOrder === 'row') {
                                                                                // Row order: A1, A2, A3, ..., A12, B1, B2, ...
                                                                                sampleIndexInPlate = rowIndex * 12 + colIndex
                                                                            } else {
                                                                                // Column order (default): A1, B1, C1, ..., H1, A2, B2, ...
                                                                                sampleIndexInPlate = colIndex * 8 + rowIndex
                                                                            }
                                                                            
                                                                            const globalSampleIndex = startSampleIndex + sampleIndexInPlate
                                                                            const sample = orderInfo.samples[globalSampleIndex]
                                                                            const hasSample = sample !== undefined
                                                                            
                                                                            return (
                                                                                <div
                                                                                    key={`${row}${col}`}
                                                                                    style={{
                                                                                        minHeight: "38px",
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        backgroundColor: hasSample ? "#388087" : "white",
                                                                                        color: hasSample ? "white" : "#999",
                                                                                        border: "1px solid #ccc",
                                                                                        borderRadius: "2px",
                                                                                        fontSize: "9px",
                                                                                        lineHeight: "1.2",
                                                                                        cursor: "default",
                                                                                        fontWeight: hasSample ? "500" : "normal",
                                                                                        overflow: "hidden",
                                                                                        wordBreak: "break-word",
                                                                                        padding: "3px",
                                                                                        textAlign: "center"
                                                                                    }}
                                                                                >
                                                                                    {hasSample ? sample.sampleName : ""}
                                                                                </div>
                                                                            )
                                                                        })}
                                                                    </>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )
                                    })()}
                                </>
                            )}
                        </Flex>
                    </LabeledBox>
                    <LabeledBox title="Enter sample concentration">
                        <Flex vertical gap={20}>
                            <div style={{ width: "24em" }}>
                                <Input
                                    style={{ color: "black" }}
                                    styles={{
                                        input: { width: "7em" },
                                    }}
                                    addonBefore="Sample concentrations (ng/μl):"
                                    type="number"
                                    id="concentration"
                                    value={orderInfo.concentration}
                                    onChange={(e) => {
                                        const concentration = Number(e.target.value)
                                        setOrderInfo({
                                            ...orderInfo,
                                            concentration,
                                        })
                                    }}
                                    placeholder="200"
                                    // onChange={(e) => setEmail(e.target.value)}
                                    size="middle"
                                    name="concentration"
                                />
                            </div>
                        </Flex>
                    </LabeledBox>
                    { props.enterSampleNames && (
                    <LabeledBox title="Enter sample names">
                        <Flex vertical gap={20}>
                            <Row justify="space-between" style={{ marginBottom: "-1em" }}>
                                <Col xs={24} md={8}>
                                    <Space direction="vertical" size="large">
                                        <div
                                            style={{
                                                width: "100%",
                                                fontSize: "1rem",
                                            }}
                                        >
                                            Autofill{" "}
                                            <input
                                                type="number"
                                                style={{
                                                    width: "4em",
                                                    fontSize: "1rem",
                                                }}
                                                onChange={(x) => {
                                                    const samples: SampleInterface[] = []
                                                    setShowSampleNameError(false)
                                                    setErrorMessage("")
                                                    for (let i = 0; i < parseInt(x.target.value); i++) {
                                                        const name = "S-" + (i + 1)
                                                        samples.push({
                                                            sampleName: name,
                                                        })
                                                    }
                                                    setOrderInfo({
                                                        ...orderInfo,
                                                        samples,
                                                        sample_names: samples.map((s) => s.sampleName).join("\n"),
                                                        sample_sequences: "",
                                                    })
                                                    
                                                }}
                                            />{" "}
                                            samples
                                        </div>
                                    </Space>
                                </Col>
                                <Col xs={24} md={16}>
                                    <GrayLabel style={{ textAlign: "right" }}>
                                        Allowed characters for sample names: letters, numbers, - _ .{" "}
                                    </GrayLabel>
                                </Col>
                            </Row>
                            {showSampleNameError && (
                                <div
                                    style={{
                                        color: "red",
                                        marginBottom: "-1em",
                                    }}
                                >
                                    Sample names cannot be the same, duplicated names will be removed.
                                </div>
                            )}
  

                            <TextArea
                                id="sampleNames"
                                placeholder="Sample names, one name per row"
                                value={orderInfo.sample_names ?? ""}
                                // onChange={(e) => setEmail(e.target.value)}
                                size="middle"
                                name="sampleNames"
                                rows={12}
                                onBlur={(e) => {
                                    // consolelog("blur", e.target.value)
                                    const names = e.target.value.trim().replace(/\n+$/, "").trim()
                                    const candidateNames = names
                                        .split("\n")
                                        .map((x) => x.trim())
                                        .filter((x) => x.length > 0)
                                    if (new Set(candidateNames).size !== candidateNames.length) {
                                        setShowSampleNameError(true)
                                    } else {
                                        setShowSampleNameError(false)
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        const names = e.currentTarget.value.trim().replace(/\n+$/, "").trim()
                                        const candidateNames = names
                                            .split("\n")
                                            .map((x) => x.trim())
                                            .filter((x) => x.length > 0)

                                        if (new Set(candidateNames).size !== candidateNames.length) {
                                            setShowSampleNameError(true)
                                        } else {
                                            setShowSampleNameError(false)
                                        }
                                    }
                                    setOrderInfo({
                                        ...orderInfo,
                                        sample_names:e.currentTarget.value,
                                    })
                                }}
                                onChange={(e) => {
                                    // console.log(e.target.value)

                                    const vs = validateSampleNames(e.target.value)

                                    if (vs.length > 0) {
                                        setErrorMessage(vs)
                                        modal.warning({
                                            content: vs,
                                        })
                                    } else {
                                        
                                        setOrderInfo({
                                            ...orderInfo,
                                            sample_names:e.currentTarget.value,
                                        })
                                    }
                                }}
                            />
                        </Flex>
                    </LabeledBox>
                    )}
                    <LabeledBox title={"Enter expected sequences"+(props.enterSampleNames? " (optional)": "")}>
                        <Flex vertical gap={20}>
                            <ul
                                style={{
                                    textAlign: "left",
                                    listStylePosition: "inside",
                                    listStyleType: "disc",
                                }}
                            >        
                                    <li style={{ lineHeight: "2rem" }}>
                                       Enter the expected sequence for each amplicon sample in fasta or excel format
                                    </li>
                                    <li style={{ lineHeight: "2rem" }}>
                                        Allowed characters for DNA bases: AGCTagct
                                    </li>
                                    <li style={{ lineHeight: "2rem" }}>
                                        Allowed characters for sample names: letters, numbers, - _ .                  
                                    </li>
                            </ul>


                            <Space size={"large"}>
                                <Button
                                    style={{ width: "16em" }}
                                    onClick={() => {
                                         setOrderInfo({
                                            ...orderInfo,
                                            sample_sequences: exampleInput2,
                                        })
                                        
                                    }}
                                >
                                    Show example FASTA input
                                </Button>
                                <Button
                                    style={{ width: "10em" }}
                                    onClick={() => {
                                        setOrderInfo({
                                            ...orderInfo,
                                            sample_names: "",
                                            sample_sequences: "",
                                            
                                        })
                                    }}
                                >
                                    Clear input
                                </Button>
                                <Upload
                                    accept=".fasta,.fas"
                                    showUploadList={false}
                                    beforeUpload={(file: FileType) => {
                                        if (file) {
                                            // console.log(file)
                                            const reader = new FileReader()
                                            reader.addEventListener("load", () => {
                                                const samples = parseFasta(reader.result as string)
                                                const names = samples.map(s => s.sampleName).join("\n")
                                                const sequences = samples.map(s => s.sequence).join("")
                                                const vs = validateSampleNames(names)
                                                if (vs.length > 0) {
                                                    
                                                    modal.warning({
                                                        content: vs,
                                                    })
                                                } else {
                                                    const vs2=validateSampleSequences(sequences)
                                                    if (vs2.length > 0) {
                                                        modal.warning({
                                                            content: vs2,
                                                        })
                                                    } else {
                                                        
                                                        setOrderInfo({
                                                            ...orderInfo,
                                                            sample_names: "",
                                                            sample_sequences: reader.result as string,
                                                            sequenceupload: reader.result as string,
                                                            
                                                        })
                                                    }
                                                    
                                                }
                                                
                                                        
                                            })
                                            reader.readAsText(file) // Adjust readAsDataURL() for images, etc.
                                        }
                                        return false
                                    }}
                                >
                                    <Button style={{ width: "10em" }}>Upload FASTA file</Button>
                                </Upload>
                                
                                <Tooltip title='Upload an excel file with columns "name" and "sequence"'>
                                    <Upload
                                        accept=".xlsx,.xls"
                                        showUploadList={false}
                                        beforeUpload={(file: FileType) => {
                                        if (file) {
                                            const reader = new FileReader()
                                            reader.addEventListener("load", () => {
                                                try {
                                                    const data = new Uint8Array(reader.result as ArrayBuffer)
                                                    const workbook = XLSX.read(data, { type: 'array' })
                                                    
                                                    // Get the first worksheet
                                                    const sheetName = workbook.SheetNames[0]
                                                    const worksheet = workbook.Sheets[sheetName]
                                                    
                                                    // Convert to JSON
                                                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][]
                                                    
                                                    if (jsonData.length < 2) {
                                                        modal.error({
                                                            title: "Invalid Excel file",
                                                            content: "Excel file must have at least a header row and one data row."
                                                        })
                                                        return
                                                    }
                                                    
                                                    // Parse header row (case insensitive)
                                                    const headerRow = jsonData[0].map(col => (col || '').toString().trim().toLowerCase())
                                                    
                                                    // Find required columns
                                                    const nameColIndex = headerRow.findIndex(col => col.includes('name'))
                                                    const sequenceColIndex = headerRow.findIndex(col => col.includes('sequence'))
                                                    let fullPartialColIndex = 1000;
                                                    
                                                    
                                                    if (nameColIndex === -1 || sequenceColIndex === -1 || fullPartialColIndex === -1) {
                                                        modal.error({
                                                            title: "Invalid Excel format",
                                                            content: "Excel file must contain columns for 'name', 'sequence', and 'full or partial' (with 'full' or 'partial' values)."
                                                        })
                                                        return
                                                    }
                                                    
                                                    // Convert to FASTA format
                                                    let fastaContent = ""
                                                    const sampleNames: string[] = []
                                                    const allSequences: string[] = []
                                                    
                                                    for (let i = 1; i < jsonData.length; i++) {
                                                        const row = jsonData[i]
                                                        
                                                        const name = (row[nameColIndex] || '').toString().trim()
                                                        const sequence = (row[sequenceColIndex] || '').toString().trim()
                                                        
                                                        if (!name || !sequence) continue
                                                        
                                                        sampleNames.push(name)
                                                        allSequences.push(sequence)
                                                        
                                                        let header = `>${name}`
                                                        if (fullPartialColIndex !== -1 && fullPartialColIndex !== 1000) {
                                                            const fullPartialValue = (row[fullPartialColIndex] || '').toString().toLowerCase()
                                                            if (fullPartialValue.includes('partial')) {
                                                                header += " | partial"
                                                            } else {
                                                                header += " | full"
                                                            }
                                                        } else {
                                                            header += " | full"
                                                        }
                                                        
                                                        fastaContent += header + "\n" + sequence + "\n"
                                                    }
                                                    
                                                    if (sampleNames.length === 0) {
                                                        modal.error({
                                                            title: "No valid data",
                                                            content: "No valid sample data found in the Excel file."
                                                        })
                                                        return
                                                    }
                                                    
                                                    // Validate converted data
                                                    const vs = validateSampleNames(sampleNames.join("\n"))
                                                    if (vs.length > 0) {
                                                        modal.warning({
                                                            content: vs,
                                                        })
                                                    } else {
                                                        const vs2 = validateSampleSequences(allSequences.join(""))
                                                        if (vs2.length > 0) {
                                                            modal.warning({
                                                                content: vs2,
                                                            })
                                                        } else {
                                                            setOrderInfo({
                                                                ...orderInfo,
                                                                
                                                                sample_sequences: fastaContent.trim(),
                                                                sequenceupload: fastaContent.trim(),
                                                            })
                                                            modal.success({
                                                                content: `Successfully imported ${sampleNames.length} samples from Excel file.`
                                                            })
                                                        }
                                                    }
                                                    
                                                } catch (error) {
                                                    console.error('Error parsing Excel file:', error)
                                                    modal.error({
                                                        title: "Error parsing Excel file",
                                                        content: "Please ensure the file is a valid Excel file (.xlsx or .xls)."
                                                    })
                                                }
                                            })
                                            reader.readAsArrayBuffer(file)
                                        }
                                        return false
                                    }}                                    >
                                        <Button style={{ width: "10em" }}>Upload Excel file</Button>
                                    </Upload>
                                </Tooltip>
                            </Space>

                            {errorMessage && (
                                <div
                                    style={{
                                        color: "red",
                                        marginBottom: "-1em",
                                    }}
                                >
                                    {errorMessage}
                                </div>
                            )}

                            <TextArea
                                id="sampleSequences"
                                placeholder="Please enter FASTA input"
                                value={orderInfo.sample_sequences ?? ""}
                                // onChange={(e) => setEmail(e.target.value)}
                                size="middle"
                                name="sampleSequences"
                                style={{
                                    fontFamily: ' "Courier New", monospace',
                                }}
                                rows={12}
                                onBlur={(e) => {}}
                                onKeyDown={(e) => {}}
                                onChange={(e) => {
                                    const samples = parseFasta(e.target.value)
                                    const names = samples.map(s => s.sampleName).join("\n")
                                    const sequences = samples.map(s => s.sequence).join("")
                                    const vs = validateSampleNames(names)
                                    if (vs.length > 0) {
                                        setErrorMessage(vs)
                                        modal.warning({
                                            content: vs,
                                        })
                                    } else {
                                        const vs2=validateSampleSequences(sequences)
                                        if (vs2.length > 0) {
                                            setErrorMessage(vs2)
                                            modal.warning({
                                                content: vs2,
                                            })
                                        } else {
                                            setErrorMessage("")
                                        }
                                        setErrorMessage(vs2)
                                    }
                                    
                                    setOrderInfo({
                                        ...orderInfo,
                                        sample_sequences: e.currentTarget.value,
                                        
                                    })
                                }}
                            />
                        </Flex>
                    </LabeledBox>
                    
                    <LabeledBox title="Summary of Sample Information">
                        <Flex vertical gap={20}>
                            <table style={{ width: "30em" }}>
                                <thead>
                                    <tr style={{ textAlign: "left" }}>
                                        <th>Sample Name</th>
                                        <th>Has Template</th>
                                        <th>Full Sequence</th>
                                        <th>Length</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderInfo.samples?.map((sample, i) => {
                                        return (
                                            <tr key={i}>
                                                <td>{sample.sampleName}</td>
                                                <td>{sample.sequence ? "Yes" : "No"}</td>

                                                <td>
                                                    {sample.sequence
                                                        ? sample.fullLength
                                                            ? "full"
                                                            : "partial"
                                                        : ""}
                                                </td>
                                                <td>{sample.sequence ? sample.sequence.length : ""}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            <Input
                                addonBefore="Order Name (optional):"
                                type="text"
                                id="ordername"
                                value={orderInfo.ordername}
                                onChange={(e) => {
                                    const orderName = e.target.value
                                    setOrderInfo({
                                        ...orderInfo,
                                        ordername: orderName,
                                    })
                                }}
                                size="middle"
                                maxLength={120}
                                name="orderName"
                            />
                            <Input
                                addonBefore="Special Notes (optional):"
                                type="text"
                                id="notes"
                                value={orderInfo.specialNotes}
                                onChange={(e) => {
                                    const notes = e.target.value
                                    setOrderInfo({
                                        ...orderInfo,
                                        specialNotes: notes,
                                    })
                                }}
                                size="middle"
                                maxLength={300}
                                name="notes"
                            />
                        </Flex>
                    </LabeledBox>
                    <div style={{ textAlign: "center", marginTop: "1em" }}>
                        <Button
                            size="large"
                            type="primary"
                            style={{
                                fontSize: "1.2rem",
                                // fontWeight: "bold",
                                backgroundColor: theme.primary,
                                boxShadow: "1px 1px 2px 2px rgba(0, 0, 0, 0.5)",
                            }}
                            icon={
                                <span
                                    className="material-symbols-outlined"
                                    style={{
                                        verticalAlign: "middle",
                                        fontSize: "1.618rem",
                                        color: "white",
                                    }}
                                >
                                    preview
                                </span>
                            }
                            onClick={() => {
                                if (errorMessage) {
                                    modal.warning({
                                        content: "Please correct the errors in the fasta input",
                                    })
                                } else if (orderInfo.samples.length === 0) {
                                    modal.warning({
                                        content: "Please enter sample names",
                                    })
                                } else if (orderInfo.productid == null) {
                                    modal.warning({
                                        content: "Please go back to select the service type",
                                    })
                                } else {
                                    // console.log("order info", orderInfo)
                                    props.setCurrentStep(2)
                                    // Scroll to top when moving to next step
                                    window.scrollTo({ top: 0, behavior: 'smooth' })
                                }
                            }}
                        >
                            Preview Order
                        </Button>
                    </div>
                </div>
            )}
        </ContainerFull>
    )
}
